import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { PRODUCT_LINES } from '../data/products';

const MARKETS = [
  'Flexible Packaging',
  'Rigid Packaging',
  'Automotive',
  'Construction',
  'Agriculture',
  'Healthcare',
  'Fiber & Textiles',
  'Electronics',
];

const POLYMERS = [
  'PET',
  'HDPE',
  'LLDPE',
  'LDPE',
  'PP',
  'PVC',
  'ABS',
  'PMMA',
  'POM',
  'PBT',
  'HIPS',
  'PA',
  'PLA',
  'rPET',
  'Other',
];

const CONVERTING_PROCESSES = [
  'Inj Molding',
  'EBM',
  'ISBM',
  'Compression Molding',
  'Blown Films',
  'Cast Films',
  'Raffia',
  'Sheets',
  'Non-Woven Fabric',
  'Pipes',
  'Wire & Cables',
];

const NEEDS = [
  { id: 'color', label: 'Color / White / Black', line: 'chromalux' },
  { id: 'uv', label: 'UV / Functional Additives', line: 'fortilux' },
  { id: 'sustainable', label: 'Sustainable / rPET', line: 'verdalux' },
  { id: 'custom', label: 'Custom Compound', line: 'formalux' },
];

const STEPS = [0, 1, 2, 3, 4];

export default function FormulationFinder({ triggerClassName = 'btn-p', triggerLabel = 'Formulation Finder' }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [market, setMarket] = useState('');
  const [polymer, setPolymer] = useState('');
  const [process, setProcess] = useState('');
  const [need, setNeed] = useState('');
  const navigate = useNavigate();

  const reset = () => {
    setStep(0);
    setMarket('');
    setPolymer('');
    setProcess('');
    setNeed('');
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const finish = () => {
    const needObj = NEEDS.find((n) => n.id === need);
    const params = new URLSearchParams({
      market,
      polymer,
      process,
      need: needObj?.label || need,
      productLine: needObj?.line || '',
    });
    close();
    navigate(`/contact?${params.toString()}`);
  };

  return (
    <>
      <button type="button" className={triggerClassName} onClick={() => setOpen(true)}>
        {triggerLabel}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="finder-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            role="presentation"
          >
            <motion.div
              className="finder-modal"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 16, scale: 0.98 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-labelledby="finder-title"
            >
              <button type="button" className="finder-close" onClick={close} aria-label="Close">
                ×
              </button>
              <div className="finder-steps" aria-hidden>
                {STEPS.map((i) => (
                  <span key={i} className={`finder-step${step >= i ? ' on' : ''}`} />
                ))}
              </div>

              <h2 id="finder-title" className="finder-title">
                Auralux Formulation Finder
              </h2>
              <p className="finder-sub">Tell us your application — we&apos;ll route you to the right team.</p>

              {step === 0 && (
                <div className="finder-panel">
                  <h3>1. End market</h3>
                  <div className="finder-chips">
                    {MARKETS.map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`finder-chip${market === m ? ' on' : ''}`}
                        onClick={() => setMarket(m)}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                  <button type="button" className="btn-p" disabled={!market} onClick={() => setStep(1)}>
                    Next
                  </button>
                </div>
              )}

              {step === 1 && (
                <div className="finder-panel">
                  <h3>2. Polymer substrate</h3>
                  <div className="finder-chips">
                    {POLYMERS.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className={`finder-chip${polymer === p ? ' on' : ''}`}
                        onClick={() => setPolymer(p)}
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                  <div className="finder-nav">
                    <button type="button" className="btn-g" onClick={() => setStep(0)}>
                      Back
                    </button>
                    <button type="button" className="btn-p" disabled={!polymer} onClick={() => setStep(2)}>
                      Next
                    </button>
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="finder-panel">
                  <h3>3. Converting process</h3>
                  <div className="finder-chips">
                    {CONVERTING_PROCESSES.map((item) => (
                      <button
                        key={item}
                        type="button"
                        className={`finder-chip${process === item ? ' on' : ''}`}
                        onClick={() => setProcess(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                  <div className="finder-nav">
                    <button type="button" className="btn-g" onClick={() => setStep(1)}>
                      Back
                    </button>
                    <button type="button" className="btn-p" disabled={!process} onClick={() => setStep(3)}>
                      Next
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="finder-panel">
                  <h3>4. What do you need?</h3>
                  <div className="finder-chips finder-chips--stack">
                    {NEEDS.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        className={`finder-chip finder-chip--wide${need === n.id ? ' on' : ''}`}
                        onClick={() => setNeed(n.id)}
                      >
                        <strong>{PRODUCT_LINES.find((l) => l.id === n.line)?.name}</strong>
                        <span>{n.label}</span>
                      </button>
                    ))}
                  </div>
                  <div className="finder-nav">
                    <button type="button" className="btn-g" onClick={() => setStep(2)}>
                      Back
                    </button>
                    <button type="button" className="btn-p" disabled={!need} onClick={() => setStep(4)}>
                      Next
                    </button>
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="finder-panel">
                  <h3>5. Ready to connect</h3>
                  <p className="finder-summary">
                    <strong>{market}</strong> · <strong>{polymer}</strong> · <strong>{process}</strong> ·{' '}
                    <strong>{NEEDS.find((n) => n.id === need)?.label}</strong>
                  </p>
                  <p className="finder-summary-note">
                    Our technical team will respond within 24 business hours with formulation guidance.
                  </p>
                  <div className="finder-nav">
                    <button type="button" className="btn-g" onClick={() => setStep(3)}>
                      Back
                    </button>
                    <button type="button" className="btn-p" onClick={finish}>
                      Continue to Contact
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
